import React, { useState, useEffect } from 'react';
import { Truck, Users, MessageCircle, CheckCircle, MapPin } from 'lucide-react';
import api from '../../api/axios';

const BolsaTrabajo = () => {
    const [branches, setBranches] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState('Pachuca');
    const [benefitIndex, setBenefitIndex] = useState(0);

    useEffect(() => {
        const fetchBranches = async () => {
            try {
                const res = await api.get('/branches');
                const raw = res.data?.data || res.data;
                setBranches(Array.isArray(raw) ? raw : []);
            } catch (error) {
                console.error("Error al cargar sucursales:", error);
                setBranches([]);
            }
        };
        fetchBranches();
    }, []);

    const handleWhatsApp = (job) => {
        const safeBranches = Array.isArray(branches) ? branches : [];
        // Buscar la sucursal seleccionada por nombre (match parcial, sin importar mayúsculas)
        const matchedBranch = safeBranches.find(b =>
            b && b.name && (
                b.name.toLowerCase().includes(selectedBranch.toLowerCase()) ||
                (b.city && b.city.toLowerCase().includes(selectedBranch.toLowerCase()))
            )
        );

        const phone = matchedBranch && matchedBranch.phone
            ? `52${matchedBranch.phone.replace(/\D/g, '')}`
            : null;

        if (!phone) {
            alert(`No se encontró número de WhatsApp para la sucursal ${selectedBranch}.`);
            return;
        }

        const msg = `Hola, estoy interesado en el puesto de ${job.title} en la sucursal ${selectedBranch}. ¿Me podrían dar más información?`;
        window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
    };

    const benefits = [
        { title: "Beneficios", subtitle: "Estabilidad", items: ["Prestaciones de Ley", "Sueldo Competitivo", "Contratación Directa"], img: "https://images.unsplash.com/photo-1521737711867-e3b97375f902?q=80&w=1000&h=650&fit=crop" },
        { title: "Crecimiento", subtitle: "Estadías y Servicio Social", items: ["Convenios Universitarios", "Mentoria Profesional", "Liberación de Documentos"], img: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?q=80&w=1000&h=650&fit=crop" }
    ];

    useEffect(() => {
        const fetchJobs = async () => {
            try {
                const response = await api.get('/public-jobs');
                setJobs(response.data);
            } catch (error) {
                console.error("Error al cargar vacantes:", error);
            }
        };
        fetchJobs();
    }, []);

    // --- ESTADOS Y EVENTOS PARA SWIPE (MÓVIL) Y DRAG (DESKTOP) ---
    const [dragStart, setDragStart] = useState(null);
    const [dragEnd, setDragEnd] = useState(null);
    const [isDragging, setIsDragging] = useState(false);

    const handleStart = (clientX) => {
        setDragStart(clientX);
        setDragEnd(null);
        setIsDragging(true);
    };

    const handleMove = (clientX) => {
        if (!isDragging) return;
        setDragEnd(clientX);
    };

    const handleEnd = () => {
        if (!isDragging || dragStart === null || dragEnd === null) {
            setIsDragging(false);
            return;
        }
        const distance = dragStart - dragEnd;
        const minSwipeDistance = 50; 

        if (distance > minSwipeDistance) {
            setBenefitIndex((prev) => (prev + 1) % benefits.length);
        } else if (distance < -minSwipeDistance) {
            setBenefitIndex((prev) => (prev - 1 + benefits.length) % benefits.length);
        }
        setIsDragging(false);
    };

    // Clase Maestra para los Degradados Sólidos que pediste
    const tbtGradient = `
        bg-[linear-gradient(180deg,#F1F1F1_0%,#C1C1C1_100%)] 
        dark:bg-[linear-gradient(180deg,#590000_0%,#780000_100%)]
        border border-black/5 dark:border-white/10
    `;

    return (
        <div className="min-h-screen bg-transparent py-12 px-4 md:px-12 space-y-16">

            {/* 1. CARRUSEL DE BENEFICIOS */}
            <div 
                onTouchStart={(e) => handleStart(e.touches[0].clientX)}
                onTouchMove={(e) => handleMove(e.touches[0].clientX)}
                onTouchEnd={handleEnd}
                onMouseDown={(e) => handleStart(e.clientX)}
                onMouseMove={(e) => handleMove(e.clientX)}
                onMouseUp={handleEnd}
                onMouseLeave={handleEnd}
                className={`${tbtGradient} max-w-7xl mx-auto rounded-3xl lg:rounded-[3.5rem] overflow-hidden shadow-2xl block lg:flex lg:flex-row h-auto lg:h-[500px] select-none lg:cursor-grab lg:active:cursor-grabbing`}
            >
                <div className="w-full lg:w-1/2 p-6 lg:p-12 flex flex-col justify-center items-center text-center lg:items-start lg:text-left space-y-6">
                    <span className="text-[#CE010B] dark:text-white/60 font-black uppercase tracking-[0.3em] text-sm lg:text-sm">Únete al Equipo</span>
                    <h2 className="text-4xl lg:text-5xl font-black text-black dark:text-white uppercase italic leading-none">{benefits[benefitIndex].title}</h2>
                    <h3 className="text-2xl lg:text-2xl font-bold text-gray-700 dark:text-white/80">{benefits[benefitIndex].subtitle}</h3>
                    <ul className="space-y-3 w-full flex flex-col items-center lg:items-start">
                        {benefits[benefitIndex].items.map((item, i) => (
                            <li key={i} className="flex items-center gap-3 font-black text-black dark:text-white/90 uppercase text-lg lg:text-sm">
                                <CheckCircle size={22} className="text-[#CE010B] dark:text-white shrink-0" /> {item}
                            </li>
                        ))}
                    </ul>
                    <div className="flex gap-2 justify-center lg:justify-start w-full lg:w-auto">
                        {benefits.map((_, i) => (
                            <button key={i} onClick={() => setBenefitIndex(i)} className={`h-2 transition-all rounded-full ${benefitIndex === i ? 'w-12 bg-[#CE010B] dark:bg-white' : 'bg-black/20 dark:bg-white/20 w-4'}`} />
                        ))}
                    </div>
                </div>
                <div className="w-full lg:w-1/2 bg-gray-100 relative h-auto lg:h-full">
                    <img src={benefits[benefitIndex].img} className="w-full h-auto lg:h-full lg:object-cover" alt="TBT Team" />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#F1F1F1] dark:from-[#590000] to-transparent lg:block hidden opacity-30" />
                </div>
            </div>

            {/* 2. FILTRO DE SUCURSAL */}
            <div className="text-center space-y-4 px-2">
                <h3 className="font-black uppercase tracking-widest text-white dark:text-white text-xs md:text-sm">Selecciona tu Sucursal</h3>
                <div className="flex w-full max-w-lg mx-auto p-2 bg-black/40 dark:bg-black/60 backdrop-blur-lg rounded-[20px] gap-2 border border-white/15 dark:border-white/10 shadow-2xl md:inline-flex md:w-auto">
                    {['Toluca', 'Pachuca', 'CDMX'].map(b => (
                        <button
                            key={b}
                            onClick={() => setSelectedBranch(b)}
                            className={`flex-1 md:flex-none px-4 md:px-10 py-3.5 md:py-3 rounded-[14px] font-black uppercase text-sm md:text-xs transition-all flex items-center justify-center gap-2 ${selectedBranch === b ? 'bg-[#CE010B] text-white shadow-md scale-[1.03]' : 'text-gray-300 dark:text-gray-400 hover:text-white'}`}
                        >
                            <MapPin size={16} className={selectedBranch === b ? 'text-white' : 'text-gray-400'} />
                            <span>{b}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* 3. LISTADO DE VACANTES */}
            <div className="max-w-7xl mx-auto space-y-10 pb-20">
                {jobs.length === 0 ? (
                    <p className="text-center font-black text-gray-500 uppercase tracking-widest opacity-50 py-20">
                        Cargando vacantes...
                    </p>
                ) : jobs.filter(j => j.branch === selectedBranch).length === 0 ? (
                    <div className={`${tbtGradient} rounded-3xl lg:rounded-[3.5rem] overflow-hidden p-12 lg:p-20 flex flex-col items-center justify-center text-center gap-6`}>
                        <div className="w-32 h-32 rounded-full bg-[#CE010B]/10 dark:bg-white/10 flex items-center justify-center">
                            <Users size={60} className="text-[#CE010B] dark:text-white/60" />
                        </div>
                        <div className="space-y-3">
                            <h3 className="text-5xl lg:text-6xl font-black text-black dark:text-white uppercase tracking-tight leading-none">
                                Sin vacantes por ahora
                            </h3>
                            <p className="text-2xl lg:text-xl text-gray-600 dark:text-white/60 font-medium max-w-md mx-auto leading-relaxed">
                                En este momento no hay vacantes disponibles en la sucursal <span className="font-black text-[#CE010B] dark:text-white">{selectedBranch}</span>. ¡Estate atento, pronto publicaremos nuevas oportunidades!
                            </p>
                        </div>
                        <div className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-black/5 dark:bg-white/10 border border-black/10 dark:border-white/10">
                            <div className="w-2 h-2 rounded-full bg-[#CE010B] animate-pulse" />
                            <span className="text-base font-black uppercase tracking-widest text-black dark:text-white opacity-60">Próximamente nuevas oportunidades</span>
                        </div>
                    </div>
                ) : (
                    jobs.filter(j => j.branch === selectedBranch).map(job => (
                        <div key={job.id} className={`${tbtGradient} rounded-3xl lg:rounded-[3.5rem] overflow-hidden block lg:flex lg:flex-row h-auto lg:h-[450px] group hover:shadow-2xl transition-all duration-500`}>

                            {/* CONTENIDO DE TEXTO */}
                            <div className="w-full lg:w-1/2 p-6 lg:p-12 flex flex-col justify-between items-center text-center lg:items-start lg:text-left">
                                <div className="space-y-4 w-full flex flex-col items-center lg:items-start">
                                    <div className="flex">
                                        <span className="px-4 py-1 bg-black/10 dark:bg-white/20 rounded-full text-sm lg:text-[10px] font-black uppercase tracking-widest text-black dark:text-white">{job.category}</span>
                                    </div>
                                    <h2 className="text-4xl lg:text-5xl font-black text-black dark:text-white uppercase leading-tight lg:leading-[0.9] tracking-tighter">{job.title}</h2>
                                    <div className="pt-2 w-full flex flex-col items-center lg:items-start">
                                        <p className="font-black text-sm lg:text-[10px] uppercase text-[#CE010B] dark:text-white/60 mb-2 tracking-widest">Requisitos del puesto</p>
                                        <div className="grid grid-cols-1 gap-1 text-lg lg:text-sm font-bold text-gray-800 dark:text-white italic leading-tight text-center lg:text-left">
                                            {job.requirements.split(',').map((req, i) => <p key={i}>• {req.trim()}</p>)}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-6 w-full">
                                    <div className="bg-white/50 dark:bg-black/30 backdrop-blur-sm px-4 py-2.5 rounded-xl border border-black/5 flex justify-between items-center sm:block text-center shrink-0">
                                        <p className="text-[10px] font-black uppercase opacity-50 text-black dark:text-white">Vacantes</p>
                                        <p className="text-2xl font-black text-black dark:text-white leading-none mt-0.5 sm:mt-1">{job.vacancies_count}</p>
                                    </div>
                                    <button
                                        onClick={() => handleWhatsApp(job)}
                                        className="flex-1 bg-[#CE010B] text-white h-[56px] rounded-xl font-black uppercase tracking-wider text-base lg:text-xs flex items-center justify-center gap-2 hover:bg-[#A30109] dark:hover:bg-white dark:hover:text-black transition-all shadow-xl"
                                    >
                                        <MessageCircle size={18} /> Agenda una cita
                                    </button>
                                </div>
                            </div>

                            {/* CONTENIDO DE IMAGEN */}
                            <div className="w-full lg:w-1/2 bg-gray-200 overflow-hidden relative h-auto lg:h-full">
                                <img
                                    src={job.image_url || "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?q=80&w=1000&auto=format&fit=crop"}
                                    className="w-full h-auto lg:h-full lg:object-cover group-hover:scale-110 transition-transform duration-1000"
                                    alt={job.title}
                                    loading="lazy"
                                />
                                {/* Overlay sutil para mejorar la transición visual */}
                                <div className="absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-[#F1F1F1] dark:to-[#590000] opacity-20 md:block hidden" />
                            </div>

                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default BolsaTrabajo;